import { FC, useEffect } from 'react';

import { MagritteNotificationName } from 'utils/constants/notification';

import { pluralize } from '@hh.ru/front-static-app';
import { Alert, Button, ButtonStack } from '@hh.ru/magritte-ui';
import { InfoCircleFilledSize24 } from '@hh.ru/magritte-ui/icon';

import useOnOffState from 'hooks/common/useOnOffState';
import { useRequestCancel } from 'hooks/common/useRequestCancel';
import useTrls from 'hooks/translation/useTrls';

import { fetchMultiStop } from 'components/CampaignsDashboard/utils/fetchMultiStop';
import { updateCampaignStatus } from 'components/CampaignsDashboard/utils/updateCampaignStatus';

import { CampaignFrontendStatus, CampaignsTab, setStopCampaignAlert } from 'store/models/campaignsDashboard';
import { addMagritteNotification } from 'store/models/notification';
import { useDispatch, useSelector } from 'store/utils';

const TrlKeys = {
    confirm: 'StopCampaignAlert.button.confirm',
    cancel: 'StopCampaignAlert.button.cancel',
    single: {
        title: 'StopCampaignAlert.title',
        description: 'StopCampaignAlert.description',
    },
    multi: {
        title: 'StopCampaignAlert.multi.title',
        description: 'StopCampaignAlert.multi.description',
    },
    campaigns: {
        zero: 'StopCampaignAlert.campaigns.zero',
        one: 'StopCampaignAlert.campaigns.one',
        some: 'StopCampaignAlert.campaigns.some',
        many: 'StopCampaignAlert.campaigns.many',
    },
};

export const StopCampaignAlert: FC = () => {
    const campaignIdsForStop = useSelector((state) => state.campaignsDashboard.campaignIdsForStop);
    const isAlertVisible = useSelector((state) => state.campaignsDashboard.isStopCampaignAlertVisible);
    const campaignsCurrentTab = useSelector((state) => state.campaignsDashboard.campaignsCurrentTab);

    const [isLoading, setIsLoadingOn, setIsLoadingOff] = useOnOffState(false);

    const dispatch = useDispatch();
    const trls = useTrls();
    const { abortRequest, abortSignal } = useRequestCancel();

    const isMulti = campaignIdsForStop && campaignIdsForStop.size > 1;
    const shouldShowDescription = [CampaignsTab.Active, CampaignsTab.Attention].includes(campaignsCurrentTab);

    const handleClose = () => {
        abortRequest();
        setIsLoadingOff();

        dispatch(setStopCampaignAlert({ isVisible: false, campaignIds: null }));
    };

    const handleConfirm = () => {
        if (isLoading) {
            return;
        }
        if (!campaignIdsForStop || campaignIdsForStop.size === 0) {
            dispatch(addMagritteNotification(MagritteNotificationName.CampaignChangeStatusFailed));
            handleClose();
            return;
        }

        setIsLoadingOn();

        if (isMulti) {
            abortRequest();
            void dispatch(fetchMultiStop(campaignIdsForStop, abortSignal())).finally(handleClose);
            return;
        }

        void dispatch(
            updateCampaignStatus([...campaignIdsForStop][0], CampaignFrontendStatus.Blocked, abortSignal())
        ).finally(handleClose);
    };

    useEffect(() => {
        return () => {
            abortRequest();
        };
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    const title = isMulti
        ? trls(TrlKeys.multi.title, [
              pluralize(campaignIdsForStop.size, {
                  zero: trls(TrlKeys.campaigns.zero),
                  one: trls(TrlKeys.campaigns.one),
                  some: trls(TrlKeys.campaigns.some),
                  many: trls(TrlKeys.campaigns.many),
              }),
          ])
        : trls(TrlKeys.single.title);

    return (
        <Alert
            visible={isAlertVisible}
            title={title}
            description={
                shouldShowDescription
                    ? trls(isMulti ? TrlKeys.multi.description : TrlKeys.single.description)
                    : undefined
            }
            buttons={
                <ButtonStack stretched layout="vertical">
                    <Button
                        mode="primary"
                        style="accent"
                        onClick={handleConfirm}
                        loading={isLoading}
                        data-qa="stop-alert-confirm-button"
                    >
                        {trls(TrlKeys.confirm)}
                    </Button>
                    <Button mode="secondary" style="accent" onClick={handleClose} data-qa="stop-alert-cancel-button">
                        {trls(TrlKeys.cancel)}
                    </Button>
                </ButtonStack>
            }
            onClose={handleClose}
        >
            <InfoCircleFilledSize24
                initialColor="tertiary"
                backgroundStyle="secondary"
                padding={12}
                borderRadius="halfHeight"
            />
        </Alert>
    );
};
